import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { ProjectAddress, ProjectAddress_DB } from '@models/Models';
import AddressRow from './components/AddressRow';
import { IoIosAddCircle } from "react-icons/io";
import MyButton from '@components/MyButton';
import { useNavigate, useParams } from 'react-router-dom';
import useProject from '@hooks/useProject';
import useImage from '@hooks/useImage';
import { ProjectModel_DB, ProjectModel } from '@models/Models';
import ImageUploader from '@components/ImageUploader';
import useAddress from '@hooks/useAddress';

const EditProject = () => {
    const { projectId } = useParams();
    const [projectData, setProjectData] = useState<ProjectModel | null>(null);
    const navi = useNavigate();
    const [loading, setLoading] = useState(false);
    
    const [addressesList, setAddressesList] = useState<ProjectAddress[]>([]);

    const [clientLogo, setClientLogo] = useState<File | null>(null);
    const [clientLogoUrl, setClientLogoUrl] = useState<string | null>(null);
    const [projectLogo, setProjectLogo] = useState<File | null>(null);
    const [projectLogoUrl, setProjectLogoUrl] = useState<string | null>(null);
    const [projectName, setProjectName] = useState("");
    const [maxTime, setMaxTime] = useState("60");
    const [warningAfter, setWarningAfter] = useState("55");

    const { updateProject, getProjectByID } = useProject();
    const { uploadImage, getPublicURL } = useImage();
    const { addAddressRow, removeAddressRow } = useAddress();

    useEffect(() => {
        if (projectId) {
            projectGetter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        if (projectData) {
            setProjectName(projectData.name);
            setMaxTime(projectData.maxTime.toString());
            setWarningAfter(projectData.warningAfter.toString());
            if (projectData.addresses?.length) {
                setAddressesList(projectData.addresses);
            }
            

            const clientLogoResult = getPublicURL(projectData.logoClient ? projectData.logoClient : "");
            const projectLogoResult = getPublicURL(projectData.projectLogo ? projectData.projectLogo : "");

            setClientLogoUrl(clientLogoResult.data);
            setProjectLogoUrl(projectLogoResult.data);
        }
    }, [projectData]);

    const projectGetter = async () => {
        const projectResult = await getProjectByID(projectId as string);
        
        if (!projectResult.success) {
            alert(projectResult.message);
            return;
        }

        setProjectData(projectResult.data);
    };

    const handleClientLogoChange = (file: File | null) => {
        setClientLogo(file);
    };
    const handleProjectLogoChange = (file: File | null) => {
        setProjectLogo(file);
    };

    const handleAddAddressRow = async () => {
        try {
            setLoading(true);

            const newAddress: ProjectAddress_DB = {
                city: "",
                country: "Deutschland",
                hausnummer: "",
                stiege: "",
                street: "",
                zipCode: "",
            };

            const result = await addAddressRow(newAddress);

            if (!result.success || !result.data?.id) {
                alert(result.message);
                return;
            }

            const createdAddress = result.data as ProjectAddress;
            const addressIds = addressesList.map(address => address.id);
            const updateProjectResult = await updateProject(projectId as string, {
                addresses: [...addressIds, createdAddress.id],
            });

            if (!updateProjectResult.success) {
                await removeAddressRow(createdAddress.id);
                alert(updateProjectResult.message);
                return;
            }

            setAddressesList(prevList => [...prevList, createdAddress]);
        }finally {
            setLoading(false);
        };
    };

    const onAddressSave = async (updatedAddress: ProjectAddress_DB, id: string) => {
        setAddressesList(prevList => prevList.map(address => 
            address.id === id ? { ...address, ...updatedAddress } : address
        ));
    }

    const handleRemoveAddressRow = async (index: number) => {
        const addressToRemove = addressesList[index];
        if (!addressToRemove) return;

        const remainingAddressIds = addressesList
            .filter(address => address.id !== addressToRemove.id)
            .map(address => address.id);
        const updateProjectResult = await updateProject(projectId as string, {
            addresses: remainingAddressIds,
        });

        if (!updateProjectResult.success) {
            alert(updateProjectResult.message);
            return;
        }

        const removeAddressResult = await removeAddressRow(addressToRemove.id);
        if (!removeAddressResult.success) {
            alert(removeAddressResult.message);
            return;
        }

        setAddressesList(prevList => prevList.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        let clientUrl = clientLogoUrl;
        let projectURL = projectLogoUrl;
    
        if (clientLogo) {
            // If user uploaded a new file, upload it
            const logoUploadResult = await uploadImage(clientLogo);
            if (!logoUploadResult.success) {
                alert(logoUploadResult.message);
                return;
            }
            clientUrl = logoUploadResult.data as string;
        } else if (clientLogoUrl === null) {
            // If user removed the logo, set it to an empty string
            clientUrl = "";
        }

        if (projectLogo) {
            // If user uploaded a new file, upload it
            const logoUploadResult = await uploadImage(projectLogo);
            if (!logoUploadResult.success) {
                alert(logoUploadResult.message);
                return;
            }
            projectURL = logoUploadResult.data as string;
        } else if (clientLogoUrl === null) {
            // If user removed the logo, set it to an empty string
            projectURL = "";
        }

        const editedProject: Partial<ProjectModel_DB> = {
            name: projectName,
            ...(clientUrl !== clientLogoUrl && { logoClient: clientUrl }),
            ...(projectURL !== projectLogoUrl && { projectLogo: projectURL }),
            addresses: addressesList.map(address => address.id),
            maxTime: parseInt(maxTime),
            warningAfter: parseInt(warningAfter),
        };
    
        const editProjectResult = await updateProject(projectId as string, editedProject);
    
        if (!editProjectResult.success) {
            alert(editProjectResult.message);
            return;
        }
    
        navi(`/projekt-texte/${editProjectResult.data?.id}`);
    };
    

    return (
        <Container className="myCont">
            <Row>
                <Col className='d-flex align-items-center justify-content-center'>
                    <ImageUploader
                        imageUrl={clientLogoUrl}
                        onImageChange={handleClientLogoChange}
                    />
                </Col>
                <Col>
                    <Form.Group>
                        <Form.Control
                            placeholder='Projekt Bezeichnung'
                            className='p-1 myBorder d-flex flex-1 w-100'
                            onChange={(e) => setProjectName(e.target.value)}
                            value={projectName}
                            as="textarea"
                            rows={3}
                        />
                    </Form.Group>
                </Col>
                <Col className='d-flex align-items-center justify-content-center'>
                    <ImageUploader
                        imageUrl={projectLogoUrl}
                        onImageChange={handleProjectLogoChange}
                    />
                </Col>
            </Row>
            <Row className="my-3">
                <Col xs={12} lg={9}>
                    <Row>
                        <Col lg={8}>
                            <Row>
                                <Col>
                                    <h2>Straße</h2>
                                </Col>
                                <Col xs={2} className='p-0'>
                                    <h2>PLZ</h2>
                                </Col>
                                <Col>
                                    <h2>Standort</h2>
                                </Col>
                            </Row>
                        </Col>
                        <Col lg={2}><h2>Haus Nr.</h2></Col>
                        <Col lg={1}><h2>Stiege</h2></Col>
                    </Row>
                    {addressesList.map((address, index) => (
                        <AddressRow
                            key={index}
                            index={index}
                            item={address}
                            onRemove={handleRemoveAddressRow}
                            onAddressSave={onAddressSave}
                        />
                    ))}
                    <Row className="my-3">
                        <Col className="d-flex justify-content-center align-items-center">
                            <button
                                type="button"
                                className="address-add-button"
                                onClick={handleAddAddressRow}
                            >
                                <IoIosAddCircle aria-hidden="true" />
                                <span>Adresse hinzufügen</span>
                            </button>
                        </Col>
                    </Row>
                </Col>
                <Col xs={12} lg={3}>
                    <Form.Group className="d-flex align-items-center gap-2 mb-2">
                        <Form.Control
                            placeholder="60"
                            value={maxTime}
                            onChange={(e) => setMaxTime(e.target.value)}
                            type="number"
                            className="myBorder w-25"
                        />
                        <Form.Label>Max. Ladedauer</Form.Label>
                    </Form.Group>
                    <Form.Group className="d-flex align-items-center gap-2">
                        <Form.Control
                            placeholder="55"
                            value={warningAfter}
                            onChange={(e) => setWarningAfter(e.target.value)}
                            type="number"
                            className="myBorder w-25"
                        />
                        <Form.Label>Warnung nach</Form.Label>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="d-flex justify-content-end gap-3">
                    <MyButton title="Zurück" disabled={false} isSecundary={true} onClick={() => navi("/")} />
                    <MyButton title="Weiter" disabled={false} isSecundary={false} onClick={handleSubmit} />
                </Col>
            </Row>
        </Container>
    );
};

export default EditProject;
